import { useState, useEffect } from 'react';
//import history from '../../managers/history';
import {
  Paper,
  Typography,
  CircularProgress,
  Button,
  IconButton,
  Box,
  FormControl,
  Select,
  MenuItem,
  SelectChangeEvent,
} from '@mui/material';
import RichTextEditor from '../../common/components/RichTextEditor/RichTextEditor';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { marked } from 'marked';
import { editContent, updateDraftStatus, VersionData } from './IdeationService';
import ShowToast from '../../common/components/ShowToast';
import { ToastTypeEnum } from '../../constants/solutionXPLR';
import { fieldAPIKeyMap } from '../NewIdeationXPLR/NewIdeation/constants';
import { KeyboardArrowDownOutlined as KeyboardArrowDownOutlinedIcon } from '@mui/icons-material';

// Interfaces
interface UseCaseVersion {
  version: number;
  timestamp: number;
  label: string;
}

interface BenefitItem {
  title: string;
  impact: string;
  reasoning: string;
}

interface VersionDataItem {
  version: string;
  label: string;
}

interface UseCaseData {
  id: string;
  title: string;
  problem_statement: string;
  overview: string;
  key_problem_areas: string[];
  complexity: string;
  reasoning: string;
  current_process?: string;
  timeline?: string;
  benefits: BenefitItem[];
  current_version: string;
  versions: UseCaseVersion[];
  hackette_score: number;
  user_score: number;
  transformation_type: string;
  sub_heading?: string;
  impact?: { label: string; value: number }[];
  confidence?: { label: string; value: number }[];
  ease?: { label: string; value: number }[];
}

interface IceScore {
  ice_score: string | number;
  impact: string | number;
  confidence: string | number;
  ease: string | number;
}

interface UseCaseDetails {
  [key: string]: string | number | boolean | Array<string | number> | IceScore;
}

interface CoiUseCaseProps {
  useCaseId?: string;
  selectedVersion?: string;
  onVersionChange?: (version: string) => void;
  versionData: VersionData;
  useCaseDetails: UseCaseDetails;
  updateChats?: () => void;
  isSending?: boolean;
  field: string;
  setActiveStep: (step: string) => void;
  fileSummary: string;
  editorContent: string;
  setEditorContent: (content: string) => void;
  isSendingToSolutionXPLR: boolean;
  setIsSendingToSolutionXPLR: (value: boolean) => void;
  handleSendUseCaseData: () => void;
  reportLoading?: boolean;
}

const CoiUseCase = ({
  useCaseId = '',
  selectedVersion = '',
  versionData,
  useCaseDetails,
  updateChats,
  isSending = false,
  field,
  onVersionChange,
  fileSummary,
  editorContent,
  setEditorContent,
  reportLoading,
  // isSendingToSolutionXPLR,
  // handleSendUseCaseData,
}: CoiUseCaseProps) => {
  const [useCaseData, setUseCaseData] = useState<UseCaseData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [currentVersion, setCurrentVersion] = useState<string>(selectedVersion);
  const solutionData = useSelector((state: RootState) => state?.solutionXPLR);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [prevVersionDataLength, setPrevVersionDataLength] = useState<number>(0); // ADDED: Track previous version data length
  const [isVersionDataUpdating, setIsVersionDataUpdating] = useState<boolean>(false); // ADDED: Track if version data is updating
  const [, setUseCaseDetails] = useState<UseCaseDetails>(useCaseDetails);
  const isSelected = Boolean(selectedVersion);
  const versionedContent: VersionDataItem[] = Object.keys(versionData).map((version) => ({
    version,
    label: `v${version?.split('.')?.[2] || ''}`,
  }));

  const latestVersion = versionedContent[versionedContent.length - 1];
  const setHtmlContent = async (data: string = '') => {
    const html = await marked.parse(data);
    setEditorContent(html);
  };

  // Load use case data
  useEffect(() => {
    loadUseCaseData(useCaseId, currentVersion);
  }, [useCaseId, currentVersion]);

  // Update version when prop changes
  useEffect(() => {
    setCurrentVersion(selectedVersion);
    setHtmlContent(versionData?.[selectedVersion?.toString()]?.summary || fileSummary || '');
  }, [selectedVersion, versionData]);
  useEffect(() => {
    const currentVersionDataLength = Object.keys(versionData || {}).length;

    // If isSending is true and versionData length changed, it means data is being updated
    if (isSending && currentVersionDataLength !== prevVersionDataLength) {
      setIsVersionDataUpdating(true);
    } else if (!isSending) {
      // When isSending becomes false, wait a bit then hide loader
      setTimeout(() => {
        setIsVersionDataUpdating(false);
      }, 500);
    }

    setPrevVersionDataLength(currentVersionDataLength);
  }, [versionData, isSending, prevVersionDataLength]);

  const loadUseCaseData = async (id: string, version: string) => {
    setIsLoading(true);
    try {
      // TODO: Call actual API service
      // const data = await CoiUseCaseService.getInstance().getUseCaseData(id, version);

      // Mock data for now
      const mockData: UseCaseData = {
        id: id,
        sub_heading: 'AI Opportunity Discovery',
        title: 'Accounts Payable Invoice Processing',
        problem_statement:
          'We are facing delays in processing vendor invoices because our Accounts Payable process is mostly manual. This leads to long approval times and sometimes late supplier payments.',
        overview:
          'Businesses that rely on manual or semi-automated Accounts Payable (AP) processes face significant operational inefficiencies, high costs, and increased financial risks. The lack of a centralized, intelligent system for invoice ingestion, validation, and approval creates process bottlenecks and limits visibility into cash flow and liabilities, ultimately straining supplier relationships and exposing the organization to compliance issues.',
        key_problem_areas: [
          'Manual Data Entry: AP teams spend excessive time manually keying in data from diverse invoice formats (PDF, paper, email), a process that is slow, error-prone, and does not scale as business grows.',
          "Complex Approval Workflows: Invoices are often routed for approval via email or physical hand-offs, resulting in long delays, a lack of transparency into an invoice's status, and difficulty enforcing approval hierarchies.",
          'Difficult Invoice Matching: Manually matching invoices to purchase orders (POs) and goods-receipt notes (GRNs) is a time-consuming and meticulous task, leading to frequent discrepancies and payment delays.',
          'Poor Financial Visibility: Finance leadership lacks a real-time view of outstanding invoices and accrued liabilities, making accurate cash flow forecasting and budget management challenging.',
          'Strained Supplier Relationships: Inefficient processing leads to late payments, which damages supplier trust and disqualifies the company from capturing valuable early payment discounts.',
          'Compliance and Fraud Risk: Manual processes lack robust audit trails, increasing the risk of duplicate payments, non-compliance with internal policies, and potential invoice fraud.',
        ],
        complexity: 'High',
        reasoning:
          'The solution requires deep integration with a core Enterprise Resource Planning (ERP) system, involves redesigning fundamental financial workflows, and impacts multiple stakeholders including AP, procurement, and business unit leaders. It demands stringent security and data accuracy to meet financial audit standards.',
        benefits: [
          {
            title: 'Cost Savings',
            impact: 'High',
            reasoning:
              'Directly reduces manual labor costs, eliminates late payment fees, and enables the capture of early payment discounts, providing a clear and measurable return on investment.',
          },
          {
            title: 'Process Productivity',
            impact: 'High',
            reasoning:
              'Dramatically accelerates the invoice-to-pay lifecycle from weeks to days. Automation eliminates bottlenecks and allows the AP team to manage a higher volume of invoices without increasing headcount.',
          },
        ],
        current_version: version,
        versions: [
          { version: 1, timestamp: Date.now() - 86400000, label: 'v1' },
          { version: 3, timestamp: Date.now(), label: 'v3' },
          { version: 5, timestamp: Date.now(), label: 'v5' },
        ],
        impact: [
          { label: '6', value: 6 },
          { label: '7', value: 7 },
          { label: '8', value: 8 },
        ],
        confidence: [
          { label: '6', value: 6 },
          { label: '7', value: 7 },
          { label: '8', value: 8 },
        ],
        ease: [
          { label: '6', value: 6 },
          { label: '7', value: 7 },
          { label: '8', value: 8 },
        ],
        hackette_score: 7.5,
        user_score: -1,
        transformation_type: 'TRANSFORMATIVE',
      };

      setUseCaseData(mockData);
    } catch (error) {
      console.error('Error loading use case data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleVersionChange = (event: SelectChangeEvent<string>) => {
    const version = event.target.value as string;
    setCurrentVersion(version);
    if (onVersionChange) {
      onVersionChange(version);
    }
  };

  const handleEditAndRestore = async (editorContent: string, isRestore: boolean = false) => {
    try {
      const organizationId = localStorage.getItem('current_organization') || '';
      const payload = {
        query: {
          report: editorContent,
          impact: useCaseDetails?.impact || '',
          user_input: '',
        },
        action: isRestore ? 'restore' : 'edit',
        version: currentVersion,
        tenantId: '6764029052789d00255f17a1',
        useCaseId: useCaseId,
        field_type: fieldAPIKeyMap[field],
        organization_id: organizationId,
      };

      await editContent(payload);

      if (!isRestore && useCaseDetails?.status === 'solutioning') {
        try {
          await updateDraftStatus(useCaseId, { status: 'report-generated' });
          setUseCaseDetails((prev: any) => ({
            ...prev,
            status: 'report-generated',
          }));
          ShowToast({
            message: 'Report updated and status set to Report Generated',
            type: ToastTypeEnum.Success,
          });
        } catch (statusErr) {
          console.error('Error updating draft status:', statusErr);
        }
      }
      if (updateChats) {
        updateChats();
      }
      ShowToast({
        message: 'Content edited successfully',
        type: ToastTypeEnum.Success,
      });
    } catch (error: unknown) {
      console.error('Error editing content:', error);
      ShowToast({
        message: 'Failed to edit content',
        type: ToastTypeEnum.Error,
      });
    }
  };

  const handleCopyToClipboard = async () => {
    try {
      if (!editorContent) {
        ShowToast({
          message: 'Nothing to copy. Please add some content first.',
          type: ToastTypeEnum.Warn,
        });
        return;
      }

      // HTML stays exactly as-is
      const htmlBlob = new Blob([editorContent], { type: 'text/html' });

      // Plain text fallback — required by Chrome
      const plainText = editorContent.replace(/<[^>]*>/g, '');
      const textBlob = new Blob([plainText], { type: 'text/plain' });

      const clipboardItem = new ClipboardItem({
        'text/html': htmlBlob,
        'text/plain': textBlob, // REQUIRED for Chrome to paste HTML
      });

      await navigator.clipboard.write([clipboardItem]);

      ShowToast({
        message: 'Copied to clipboard successfully!',
        type: ToastTypeEnum.Success,
      });
    } catch (error) {
      ShowToast({
        message: `Failed to copy text. Please try again.\nError: ${error}`,
        type: ToastTypeEnum.Error,
      });
    }
  };

  // const versions = Object.keys(versionData);
  // const latestVersionNumber = versions?.sort(compareVersions).at(-1);

  // const isLatestVersion = currentVersion === latestVersionNumber;

  if (isLoading) {
    return (
      <Box
        sx={{
          flex: 1,
          height: 'calc(100vh - 100px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!useCaseData) {
    return (
      <Box
        sx={{
          flex: 1,
          height: 'calc(100vh - 20px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Typography variant="body1" color="text.secondary">
          No use case data available
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: '100%',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
      }}
    >
      {/* Header */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: '12px 12px 0 0',
          borderBottom: '1px solid #E1E1E1',
          borderColor: 'divider',
          backgroundColor: '#fff',
          pl: '15px',
          pr: '12px',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            pt: '14px',
            pb: '9px',
            height: '29px',
          }}
        >
          <Box sx={{ display: 'flex', gap: 2, justifyItems: 'center' }}>
            <Typography
              sx={{
                fontWeight: 600,
                fontSize: '15px',
                color: '#1a1a1a',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              {'Semantic Knowledge'}
            </Typography>
            {isSelected && !isSending && !isVersionDataUpdating && (
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {!isEditing ? (
                  versionedContent?.length > 0 && (
                    <FormControl
                      size="small"
                      sx={{
                        minWidth: 56,
                        background: '#E8EEFE',
                        borderRadius: '8px',
                        border: '1px solid #B9B9B9',
                        height: '30px',
                      }}
                    >
                      <Select
                        value={currentVersion}
                        onChange={handleVersionChange}
                        IconComponent={KeyboardArrowDownOutlinedIcon}
                        sx={{
                          fontSize: '13px',
                          fontWeight: 600,
                          '& .MuiOutlinedInput-notchedOutline': {
                            border: 'none',
                          },
                          height: '30px',
                        }}
                      >
                        {versionedContent.map((v: VersionDataItem) => (
                          <MenuItem
                            key={v.version}
                            value={v.version}
                            sx={{ fontSize: '13px', fontWeight: 600 }}
                          >
                            {v.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )
                ) : (
                  <Typography
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      background: '#E8EEFE',
                      border: '1px solid #B9B9B9',
                      borderRadius: '8px',
                      height: '30px',
                      fontSize: '14px',
                      padding: '0 10px',
                    }}
                  >
                    Edit Mode
                  </Typography>
                )}
              </Box>
            )}
          </Box>
          {!isEditing && !isSending && !isVersionDataUpdating && (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              {Object.keys(versionData || {})?.length > 0 &&
                (currentVersion === latestVersion?.version ? (
                  <IconButton onClick={() => setIsEditing(!isEditing)} disabled={!isSelected}>
                    <img src="/images/edit_icon_coi.svg" alt="edit" />
                  </IconButton>
                ) : (
                  selectedVersion &&
                  currentVersion !== latestVersion?.version && (
                    <Button
                      variant="outlined"
                      sx={{
                        textTransform: 'none',
                        mr: 2,
                        background: '#EEF5FF',
                        borderRadius: '8px',
                        border: 'none',
                      }}
                      onClick={() => {
                        handleEditAndRestore(editorContent, true);
                        setIsEditing(false);
                      }}
                    >
                      Restore this Version
                    </Button>
                  )
                ))}
              {editorContent?.length > 0 && (
                <IconButton disabled={!isSelected} onClick={handleCopyToClipboard}>
                  <img src="/images/copy-icon.svg" alt="download" />
                </IconButton>
              )}
            </Box>
          )}
        </Box>
      </Paper>

      {/* Content Area */}
      {isSelected ? (
        <Paper
          elevation={0}
          sx={{
            flex: 1,
            overflowY: 'hidden',
            py: 0,
            pl: '0px',
            backgroundColor: '#ffffff',
            borderRadius: '0 0 12px 12px',
            '&::-webkit-scrollbar': {
              width: '6px',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: '#ccc',
              borderRadius: '3px',
            },
          }}
        >
          {(() => {
            const shouldShowLoader =
              isSending || isVersionDataUpdating || (isSelected && !editorContent);
            if (shouldShowLoader) {
              return (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: 'calc(100% - 45px)',
                    flexDirection: 'column',
                    gap: 2,
                    mt: '20px',
                  }}
                >
                  {/* <CircularProgress size={40} /> */}
                  <>
                    <img
                      src="/images/Star_Loader.gif"
                      alt="Loading"
                      style={{
                        width: '200px',
                        height: 'auto',
                        maxWidth: '100%',
                      }}
                    />
                    <Typography
                      sx={{
                        fontSize: '16px',
                        fontFamily: 'poppins',
                        fontStyle: 'semibold',
                        fontWeight: 600,
                        color: '#757575',
                        mt: -9,
                      }}
                    >
                      Analysing the provided input
                    </Typography>
                  </>
                </Box>
              );
            }

            return (
              <>
                <Box
                  sx={{
                    width: '100%',
                    height: '100%',
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  <RichTextEditor
                    key={`version-${currentVersion}`}
                    content={editorContent}
                    onChange={setEditorContent}
                    height="100%"
                    width="100%"
                    maxHeight="100vh"
                    readOnly={!isEditing}
                    toolbarOptions={{
                      link: false,
                      image: false,
                      codeBlock: false,
                      textColor: false,
                      fontFamily: false,
                    }}
                    solutionId={solutionData?.id}
                    orgId={solutionData?.id || ''}
                    processStatus={'ELABORATED_SUMMARY_GENERATED'}
                    subProcessStatus={''}
                    enableComments={false}
                    // disable hover border color for the COI editor
                    disableHover={true}
                  />
                </Box>
              </>
            );
          })()}
        </Paper>
      ) : (
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: '#ffffff',
            gap: 2,
          }}
        >
          {isSending || isVersionDataUpdating ? (
            <>
              <img
                src="/images/Star_Loader.gif"
                alt="Loading"
                style={{
                  width: '200px',
                  height: 'auto',
                  maxWidth: '100%',
                }}
              />
              <Typography
                sx={{
                  fontSize: '16px',
                  fontFamily: 'poppins',
                  fontStyle: 'semibold',
                  fontWeight: 600,
                  color: '#757575',
                  mt: -4,
                }}
              >
                Analysing the provided input
              </Typography>
            </>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
              <img
                src="/images/star-static.svg"
                alt="Select a use case"
                style={{ height: 'auto', maxWidth: '100%' }}
              />
              <Typography
                sx={{
                  fontSize: '16px',
                  fontFamily: 'poppins',
                  fontStyle: 'semibold',
                  fontWeight: 600,
                  color: '#757575',
                  mt: -2,
                  mr: -2,
                }}
              >
                {`${reportLoading ? 'Loading...' : 'waiting for user input'}`}
              </Typography>
            </Box>
          )}
        </Box>
      )}

      <Box
        sx={{
          bottom: 0,
          background: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          // borderTop: '1px solid #B9B9B9',
          // p: 1,
          // height: '50px',
        }}
      >
        {isEditing ? (
          <>
            <Button
              variant="outlined"
              sx={{
                textTransform: 'none',
                mr: 2,
                mt: 0.75,
                background: '#FFFFFFF',
                borderRadius: '8px',
                border: '1px solid #1A55F9 ',
              }}
              onClick={() => {
                setIsEditing(false);
                setHtmlContent(
                  versionData?.[selectedVersion?.toString()]?.summary || fileSummary || ''
                );
              }}
            >
              Discard
            </Button>
            <Button
              variant="contained"
              sx={{
                textTransform: 'none',
                mr: 2,
                mt: 0.75,
                background: '#1B54F8',
                color: 'white',
                borderRadius: '8px',
                border: 'none',
              }}
              onClick={() => {
                handleEditAndRestore(editorContent, false);
                setIsEditing(false);
              }}
            >
              Save
            </Button>
          </>
        ) : null}
      </Box>
    </Box>
  );
};

export default CoiUseCase;
